/**
 * ModalManager: Handles queuing and prioritization of multiple modals.
 * Supports both Remodal (jQuery-based) and HTML5 <dialog> elements.
 */

// Configuration for modal timing and behavior.
// Grouped here to facilitate future adjustments tied to animation speeds or UX requirements.
const MODAL_CONFIG = {
  /**
   * Delay (ms) between dispatching modals.
   * - UX: Prevents "popping" stress by providing a psychological buffer after a user closes a modal.
   * - Technical: Ensures the previous modal's backdrop cleanup and fade-out animations are fully completed.
   * Provisional value: 100ms (to be adjusted based on interaction feedback).
   */
  QUEUE_DISPATCH_DELAY_MS: 100,

  /**
   * Delay (ms) to allow multiple modal enqueues during page load to "settle" for priority sorting.
   * - Context: A ~12ms gap between DOMContentLoaded and jQuery's ready event was observed in development.
   * - Safety: 200ms provides a sufficient buffer for varied device performance and network latency
   *   to ensure all initial modals are collected before the first dispatch.
   */
  INITIAL_SETTLING_DELAY_MS: 200,
};

/**
 * Adapter for jQuery-based Remodal instances.
 * Implements the open() method to return a Promise that resolves when the modal is closed.
 */
class RemodalAdapter {
  /**
   * @param {Object} instance Remodal instance (returned by $(el).remodal())
   * @param {number} priority
   */
  constructor(instance, priority) {
    this.instance = instance;
    this.priority = priority;
  }

  /**
   * Extract the raw DOM element from the Remodal instance for duplicate checking
   * @returns {HTMLElement}
   */
  get element() { return this.instance.$modal[0]; }

  open() {
    return new Promise((resolve) => {
      // Listen for the closed event to trigger the next modal in the queue
      $(this.element).one('closed', () => resolve());
      this.instance.open();
    });
  }
}

/**
 * Adapter for native HTML5 <dialog> elements.
 * Implements the open() method to return a Promise that resolves when the modal is closed.
 */
class DialogAdapter {
  constructor(element, priority) {
    this.element = element;
    this.priority = priority;
  }

  open() {
    return new Promise((resolve) => {
      // Listen for the close event to trigger the next modal in the queue
      this.element.addEventListener('close', () => resolve(), { once: true });
      this.element.showModal();
    });
  }
}

class ModalQueueManager {
  constructor() {
    this.queue = [];
    this.isDispatching = false;
    this.dispatchTimer = null;
    this.isSettled = false;
  }

  get isEmpty() { return (this.queue.length === 0); }

  /**
   * Returns true if a modal with the given element is already in the queue.
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  isEnqueued(element) {
    return this.queue.some(item => item.element === element);
  }

  /**
   * Request a modal to be shown by adding it to the queue.
   * Duplicate requests for the same modal element are ignored.
   * @param {RemodalAdapter|DialogAdapter} adapter
   */
  enqueue(adapter) {
    if (this.isEnqueued(adapter.element)) return;

    this.queue.push(adapter);
    this.sortQueue();
    this.scheduleDispatch();
  }

  sortQueue() {
    this.queue.sort((a, b) => b.priority - a.priority);
  }

  scheduleDispatch() {
    if (this.isDispatching || this.dispatchTimer) return;

    this.dispatchTimer = setTimeout(() => {
      this.dispatchTimer = null;
      this.isSettled = true;
      this.dispatchNext();
    }, this.determineSchedulingDelayMs());
  }

  /**
   * Determine the delay before dispatching from the queue.
   * A longer delay is used during initial page load to collect all enqueues.
   * This ensures that enqueues triggered at different timings (e.g., native
   * DOMContentLoaded vs. jQuery's document ready) are all queued before the
   * first modal is dispatched, allowing them to be shown in the correct priority order.
   */
  determineSchedulingDelayMs() {
    return this.isSettled ? 0 : MODAL_CONFIG.INITIAL_SETTLING_DELAY_MS;
  }

  /**
   * Dispatches the next modal in the queue and ensures sequential display.
   * This method waits for each modal to finish (open to closed) before
   * scheduling the next one with a short delay.
   */
  async dispatchNext() {
    if (this.isDispatching || this.isEmpty) return;

    this.isDispatching = true;
    const nextModal = this.queue.shift();

    try {
      // nextModal.open() returns a Promise representing the modal's lifecycle (from open to closed).
      // Synchronous errors within open() are caught by the Promise and handled here to prevent the queue from stalling.
      await nextModal.open();
    } catch (error) {
      // Skip the failed modal and continue dispatching from the queue
      console.error('Failed to open modal:', nextModal.element, error);
    } finally {
      // Small delay to ensure the previous modal's backdrop/cleanup is finished
      setTimeout(() => {
        this.isDispatching = false;
        this.dispatchNext();
      }, MODAL_CONFIG.QUEUE_DISPATCH_DELAY_MS);
    }
  }
}

// Global priorities (Higher values have higher priority)
export const ModalPriority = {
  DENTAL_PROFESSIONAL_CONFIRM: 100, // 歯科医療従事者確認（最優先）
  FACEBOOK_LOGIN: 90,               // Facebook認証・ログイン処理
  GMO_CARD_FORM: 80,                // GMOカード情報入力フォーム
  PRICING_FLOW: 70,                 // 価格体系選択(SP)
  ADVERTISEMENT: 10,                // 広告
  JOHNSON_AND_JOHNSON: 0,           // J&J広告 (廃止予定)
};

export const ModalManager = new ModalQueueManager();
export { RemodalAdapter, DialogAdapter };
